import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart3,
  BookOpen,
  FileCheck2,
  Users,
  Crown,
  CalendarCheck,
  User as UserIcon,
  LogIn,
  UserPlus,
  ShieldCheck,
  RotateCcw,
  HelpCircle,
  Lock,
  ChevronDown,
  Menu,
  X,
  Layers,
  MapPin
} from 'lucide-react';
import { UserProfile, TierType, AuthIntent } from '../types';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { isUserAdmin } from '../lib/firebase';
import { PrePraxisBadge } from './brand/PrePraxisBadge';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userProfile: UserProfile;
  setUserProfile?: React.Dispatch<React.SetStateAction<UserProfile>>;
  openPricingModal: () => void;
  openAuthModal: (mode?: 'signin' | 'signup', intent?: AuthIntent) => void;
  dueReviewCount?: number;
  selectedExamEntry?: ExamCatalogEntry | null;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  activeTab,
  setActiveTab,
  userProfile,
  openPricingModal,
  openAuthModal,
  dueReviewCount = 0,
  selectedExamEntry = null,
  onSelectExam
}) => {
  const isAdmin = isUserAdmin(userProfile);
  const isAuthenticated = Boolean(userProfile?.uid && userProfile.uid !== 'guest_user_1');
  const isLoggedIn = isAuthenticated;

  // Dropdown states (marketing nav only carries the Courses/Exam Catalog
  // mega-dropdown now; the old Row-1 "Exams" dropdown, search bar, and Row-2
  // "More" dropdown were removed when the two logged-out nav rows were
  // merged into a single bar -- see handover 2026-09-12).
  const [coursesMegaOpen, setCoursesMegaOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const coursesRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (coursesRef.current && !coursesRef.current.contains(event.target as Node)) {
        setCoursesMegaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExamClick = (entry: ExamCatalogEntry) => {
    setCoursesMegaOpen(false);
    setMobileMenuOpen(false);

    // Open to guests too (2026-10-08): they can browse an exam's papers and
    // try 5 free questions before signing up.
    if (onSelectExam) {
      onSelectExam(entry);
    }
    setActiveTab('papers');
  };

  // Authenticated Tabs (Logged-In)
  const authenticatedTabs = [
    {
      id: 'dashboard',
      label: isAdmin ? 'Admin' : 'Dashboard',
      icon: isAdmin ? ShieldCheck : BarChart3
    },
    { id: 'papers', label: 'Exams Directory', icon: BookOpen },
    {
      id: 'review',
      label: 'Review Queue',
      icon: RotateCcw,
      badge: dueReviewCount > 0 ? dueReviewCount : undefined
    },
    { id: 'mock-exams', label: 'Mock Exams', icon: FileCheck2 },
    { id: 'guides', label: 'Study Guides', icon: BookOpen },
    { id: 'forum', label: 'Community', icon: Users },
    { id: 'tutoring', label: '1-on-1 Tutoring', icon: CalendarCheck },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-xs">
      {isLoggedIn ? (
        /* ========================================================================= */
        /* AUTHENTICATED TOP BAR -- unchanged: logo, full authenticated tab set,     */
        /* account button / Go Unlimited CTA, mobile hamburger.                     */
        /* ========================================================================= */
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3 sm:gap-4">

            {/* Left: Brand Logo & Wordmark */}
            <div
              onClick={() => setActiveTab('dashboard')}
              className="flex items-center gap-3 cursor-pointer group shrink-0"
            >
              <div className="bg-indigo-700 w-10 h-10 rounded-xl p-1 flex items-center justify-center text-white shadow-xs group-hover:bg-indigo-800 transition-colors shrink-0">
                <PrePraxisBadge className="w-8 h-8 text-white" showLaurels={true} showCap={true} />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-800 flex items-center gap-1.5 leading-none">
                  Pre-Praxis <span className="text-indigo-600 font-extrabold">PREP</span>
                  {isAdmin && (
                    <span className="px-2 py-0.5 bg-amber-500 text-slate-950 text-[10px] font-black uppercase tracking-wider rounded shadow-xs flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-slate-950" /> ADMIN
                    </span>
                  )}
                </h1>
                <p className="text-[11px] text-slate-400 font-medium hidden sm:block mt-0.5">
                  {isAdmin
                    ? 'System Administrator Dashboard View'
                    : 'Standardized Teacher Certification Platform'}
                </p>
              </div>
            </div>

            {/* Authenticated Center Navigation */}
            <nav className="hidden lg:flex items-center gap-4">
              {authenticatedTabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 py-1 text-sm font-semibold transition-colors relative cursor-pointer ${
                      isActive
                        ? 'text-indigo-700 font-bold border-b-2 border-indigo-700'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-700' : 'text-slate-400'}`} />
                    <span>{tab.label}</span>
                    {tab.badge !== undefined && (
                      <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-slate-950 font-extrabold text-[10px] shadow-xs animate-pulse">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Right: Account + Go Unlimited */}
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openAuthModal('signin')}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs p-2 sm:px-3 sm:py-2 rounded-xl transition flex items-center gap-1.5 border border-slate-200 cursor-pointer"
                >
                  <UserIcon className="w-4 h-4 text-indigo-600" />
                  <span className="hidden sm:inline">{userProfile.displayName || 'My Account'}</span>
                </button>

                {!isAdmin && (
                  <button
                    onClick={openPricingModal}
                    className="bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-xs px-3 py-2 rounded-xl transition shadow-xs flex items-center gap-1.5 cursor-pointer"
                  >
                    <Crown className="w-3.5 h-3.5 text-amber-300" />
                    <span className="hidden sm:inline">Go Unlimited</span>
                  </button>
                )}
              </div>

              {/* Mobile Hamburger Toggle Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition cursor-pointer"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

          </div>
        </div>
      ) : (
        /* ========================================================================= */
        /* LOGGED-OUT MARKETING NAVBAR -- single consolidated bar (replaces the old  */
        /* two-row Row-1 + Row-2 layout). Full-bleed lagoon-green bar, items spread  */
        /* evenly left to right: Logo, Courses/Exam Catalog (dropdown), Study       */
        /* Guides, Community, FAQ, Log In, Get Started. "Exams" dropdown, "Mock     */
        /* Exams" link, the search bar, "More" dropdown, and "Pricing" were         */
        /* removed per Peter's 2026-09-12 request -- those exam-browsing/search    */
        /* features are gated behind login now (unaffected authenticated branch    */
        /* above still has Exams Directory / Mock Exams / Study Guides / Search    */
        /* is not present there either, it never was). Pricing is still reachable  */
        /* via the hero's "All-Access ($35/mo)" button and the pricing modal.      */
        /* ========================================================================= */
        <div className="bg-[#0E9594] w-full">
          <div className="w-full px-4 sm:px-6 lg:px-10">
            <div className="flex items-center h-16 gap-2 sm:gap-3">

              {/* Left: Brand Logo & Wordmark */}
              <div
                onClick={() => setActiveTab('landing')}
                className="flex items-center gap-2.5 cursor-pointer group shrink-0"
              >
                <div className="bg-white/15 w-9 h-9 sm:w-10 sm:h-10 rounded-xl p-1 flex items-center justify-center text-white shadow-xs group-hover:bg-white/25 transition-colors shrink-0">
                  <PrePraxisBadge className="w-7 h-7 sm:w-8 sm:h-8 text-white" showLaurels={true} showCap={true} />
                </div>
                <h1 className="text-base sm:text-lg font-black tracking-tight text-white leading-none hidden sm:block">
                  Pre-Praxis <span className="text-pink-200">PREP</span>
                </h1>
              </div>

              {/* Center: evenly-spaced marketing nav items (desktop) */}
              <nav className="hidden lg:flex flex-1 items-center justify-evenly px-2">
                {/* "Home" (2026-10-07 redesign) */}
                <button
                  onClick={() => setActiveTab('landing')}
                  className={`py-2 px-3.5 rounded-xl font-bold text-sm transition cursor-pointer ${
                    activeTab === 'landing' || activeTab === 'dashboard' ? 'text-white bg-white/15' : 'text-pink-50 hover:text-white hover:bg-white/10'
                  }`}
                >
                  Home
                </button>
                {/* "Courses / Exam Catalog" Mega-Dropdown Trigger */}
                <div className="relative" ref={coursesRef}>
                  <button
                    onClick={() => setCoursesMegaOpen(!coursesMegaOpen)}
                    className={`flex items-center gap-1.5 py-2 px-3.5 rounded-xl font-bold text-sm transition cursor-pointer ${
                      coursesMegaOpen
                        ? 'bg-white/20 text-white'
                        : 'text-pink-50 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Layers className="w-4 h-4" />
                    <span>Courses / Exam Catalog</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${coursesMegaOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* MEGA-DROPDOWN: Full Exam Family Catalog Grid */}
                  {coursesMegaOpen && (
                    <div
                      className="fixed left-4 right-4 sm:left-12 sm:right-12 md:left-24 md:right-24 lg:left-1/2 lg:-translate-x-1/2 lg:w-[960px] top-20 bg-white border border-slate-200 rounded-3xl shadow-2xl p-6 z-50 animate-in fade-in slide-in-from-top-2 max-h-[80vh] overflow-y-auto"
                    >
                      {/* Mega Dropdown Header */}
                      <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black text-indigo-700 uppercase tracking-wider">
                              Official Certification Catalog
                            </span>
                            <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                              Free Preview Available
                            </span>
                          </div>
                          <h3 className="text-base font-extrabold text-slate-900 mt-0.5">
                            State & National Educator Exam Families
                          </h3>
                        </div>
                        <button
                          onClick={() => setCoursesMegaOpen(false)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Grid of Exam Family Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                        {examCatalog.map(exam => (
                          <div
                            key={exam.id}
                            onClick={() => handleExamClick(exam)}
                            className="p-3.5 bg-slate-50 hover:bg-indigo-50/70 border border-slate-200 hover:border-indigo-300 rounded-2xl transition-all cursor-pointer group flex flex-col justify-between"
                          >
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-slate-200">
                                  <MapPin className="w-2.5 h-2.5 text-indigo-600" />
                                  {exam.state}
                                </span>
                                <span className="text-[10px] font-semibold text-slate-500 uppercase">
                                  {exam.badge}
                                </span>
                              </div>
                              <h4 className="font-extrabold text-slate-900 text-sm group-hover:text-indigo-700 transition">
                                {exam.shortLabel}
                              </h4>
                              <p className="text-[11px] text-slate-600 line-clamp-2 leading-tight">
                                {exam.subtests}
                              </p>
                            </div>

                            <div className="mt-3 pt-2 border-t border-slate-200/60 flex items-center justify-between text-[11px]">
                              <span className="font-bold text-slate-700">
                                {exam.questionsCount.split(' ')[0]} Items
                              </span>
                              <span className="font-bold text-indigo-600 group-hover:translate-x-0.5 transition flex items-center gap-1">
                                Try free preview →
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Mega Dropdown Footer */}
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span>Click any exam to access 5 free practice questions & full rubric guidelines.</span>
                        <button
                          onClick={() => {
                            setCoursesMegaOpen(false);
                            openPricingModal();
                          }}
                          className="font-bold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                        >
                          Unlock All {examCatalog.length} Families ($35/mo) →
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* "Study Guides" -- gated, opens signup with contextual intent */}
                <button
                  onClick={() => openAuthModal('signup', { actionType: 'guides', tab: 'guides', contextTitle: 'Study Guides' })}
                  className="py-2 px-3.5 rounded-xl font-bold text-sm text-pink-50 hover:text-white hover:bg-white/10 transition cursor-pointer"
                >
                  Study Guides
                </button>

                {/* "Community" -- gated, opens sign-in with contextual intent */}
                <button
                  onClick={() => openAuthModal('signin', { actionType: 'community', tab: 'forum', contextTitle: 'Educator Community Forum' })}
                  className="flex items-center gap-1.5 py-2 px-3.5 rounded-xl font-bold text-sm text-pink-50 hover:text-white hover:bg-white/10 transition cursor-pointer"
                  title="Sign in to access candidate discussions"
                >
                  <Lock className="w-3.5 h-3.5" />
                  <span>Community</span>
                </button>

                {/* "FAQ" -- open, ungated */}
                <button
                  onClick={() => setActiveTab('faq')}
                  className="py-2 px-3.5 rounded-xl font-bold text-sm text-pink-50 hover:text-white hover:bg-white/10 transition cursor-pointer"
                >
                  FAQ
                </button>
              </nav>

              {/* Right: Log In / Get Started -- pink CTAs against the green bar */}
              <div className="hidden lg:flex items-center gap-2.5 shrink-0">
                <button
                  onClick={() => openAuthModal('signin', { actionType: 'general' })}
                  className="px-4 py-2 rounded-xl border-2 border-pink-300/80 text-pink-50 hover:bg-pink-500/25 hover:border-pink-200 hover:text-white font-bold text-sm transition cursor-pointer flex items-center gap-1.5"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Log In</span>
                </button>

                <button
                  onClick={() => openAuthModal('signup', { actionType: 'general' })}
                  className="px-4 py-2 rounded-xl bg-pink-500 hover:bg-pink-600 text-white font-extrabold text-sm transition shadow-xs cursor-pointer flex items-center gap-1.5"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Get Started</span>
                </button>
              </div>

              {/* Mobile Hamburger Toggle Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden ml-auto p-2 rounded-xl text-white hover:bg-white/10 transition cursor-pointer"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MOBILE NAVIGATION DRAWER (Responsive Hamburger Menu)                      */}
      {/* Marketing (logged-out) drawer content mirrors the desktop item set above: */}
      {/* Courses/Exam Catalog list, Study Guides, Community, FAQ, then Log In /    */}
      {/* Get Started. The old Mock Exams link, search box, "Our Platform          */}
      {/* Features" / "Blog" quick links, and "All-Access Pricing" button were     */}
      {/* removed to match. Logged-in mobile behaviour (drawer content when        */}
      {/* isLoggedIn) is unchanged from before -- it simply reuses this same       */}
      {/* hamburger state and had no drawer content of its own previously either. */}
      {/* ========================================================================= */}
      {mobileMenuOpen && !isLoggedIn && (
        <div className="lg:hidden border-t border-slate-200 bg-white p-4 space-y-4 shadow-xl animate-in slide-in-from-top-2 max-h-[85vh] overflow-y-auto">
          {/* Mobile Exam Catalog List */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2">
              Courses / Exam Catalog
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
              {examCatalog.map(exam => (
                <button
                  key={exam.id}
                  onClick={() => handleExamClick(exam)}
                  className="w-full text-left p-2 rounded-xl bg-slate-50 hover:bg-indigo-50 text-xs font-semibold text-slate-800 flex items-center justify-between cursor-pointer"
                >
                  <span>{exam.shortLabel} <span className="text-slate-500 font-normal">({exam.state})</span></span>
                  <span className="text-[10px] font-bold text-indigo-600">Preview →</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Links */}
          <div className="border-t border-slate-100 pt-3 space-y-1">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openAuthModal('signup', { actionType: 'guides', tab: 'guides', contextTitle: 'Study Guides' });
              }}
              className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4 text-indigo-600" /> Study Guides
            </button>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openAuthModal('signin', { actionType: 'community', tab: 'forum', contextTitle: 'Educator Community Forum' });
              }}
              className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <Lock className="w-4 h-4 text-indigo-600" /> Community
            </button>

            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setActiveTab('faq');
              }}
              className="w-full text-left px-3 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4 text-indigo-600" /> FAQ
            </button>
          </div>

          {/* Mobile Auth Buttons */}
          <div className="border-t border-slate-100 pt-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openAuthModal('signin');
              }}
              className="w-full py-2 rounded-xl border border-slate-300 text-xs font-bold text-slate-700 text-center"
            >
              Log In
            </button>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openAuthModal('signup');
              }}
              className="w-full py-2 rounded-xl bg-pink-500 text-xs font-bold text-white text-center"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
